import React from 'react'

import {
  ElapsedTime,
  getElapsedTimeBetweenDate,
  createStringWithTimeUnit,
} from '@sketch/utils'
import {
  useGetBillingForWorkspaceNoticeQuery,
  WorkspaceMinimalFragment,
} from '@sketch/gql-types'
import { ReactComponent as LicenseIconMonochrome } from '@sketch/icons/license-diamond-monochrome-24'
import { ReactComponent as ExclamationMarkTriangleMonochromeIcon } from '@sketch/icons/exclamation-mark-triangle-monochrome-24'
import { WorkspaceSidebarNoticeContainer } from '../WorkspaceSidebarNoticeContainer'
import {
  NoticeContentTemplateFullLink,
  NoticeContentTemplateWithOptionalActionAndLearnMore,
} from '../NoticeContentTemplates'

import * as S from './WorkspaceSidebarNoticeTrial.styles'
import { WorkspaceNoticeComponentProps } from '../types'
import { SKETCH_WEBSITE } from '@sketch/env-config'
import { routes } from '@sketch/modules-common'
import { shouldNeverHappen } from '@sketch-hq/sketch-prototype-player'
import { canManageSubscription } from '../workspaceStatusUtils'

type SidebarWorkspaceNoticeTrialProps = WorkspaceNoticeComponentProps

/**
 * Component to display when we want to show a notice related to the trial (active or expired).
 * The component has 3 substates:
 * - Active with more than 15 days left
 * - Active with less than 15 days left
 * - Trial has expired.
 */
export function SidebarWorkspaceNoticeTrial({
  workspace,
}: SidebarWorkspaceNoticeTrialProps) {
  const billingQuery = useGetBillingForWorkspaceNoticeQuery({
    variables: { identifier: workspace.identifier },
  })

  const billing = billingQuery.data?.workspace.customer?.billing

  if (
    billingQuery.loading ||
    billingQuery.error ||
    !billing ||
    !billing.trialEnd
  ) {
    return null
  }

  if (billing.status === 'TRIALING') {
    const now = new Date()
    try {
      // Wrap in try in case getElapsedTimeBetweenDate returns an error
      // because endDate being set earlier than startDate just in case of data
      // inconsistency
      const elapsedTime = getElapsedTimeBetweenDate(
        now,
        new Date(billing.trialEnd)
      )
      const daysLeft = elapsedTime.toDays(
        // Use 'full' because of https://sketch.slack.com/archives/C04N2EPHUGN/p1682001080112139.
        { precision: 'full' }
      )

      if (daysLeft >= 15) {
        return (
          <ActiveTrialManyDaysLeft workspace={workspace} daysLeft={daysLeft} />
        )
      } else {
        return (
          <ActiveTrialFewDaysLeft
            workspace={workspace}
            daysLeft={daysLeft}
            elapsedTime={elapsedTime}
          />
        )
      }
    } catch (err) {
      shouldNeverHappen(err)
      return null
    }
  }

  if (billing.status === 'CANCELED') {
    return <EndedTrial workspace={workspace} />
  }

  return null
}

type ActiveTrialManyDaysLeftProps = {
  daysLeft: number
  workspace: WorkspaceMinimalFragment
}
function ActiveTrialManyDaysLeft({
  daysLeft,
  workspace,
}: ActiveTrialManyDaysLeftProps) {
  return (
    <WorkspaceSidebarNoticeContainer>
      <NoticeContentTemplateFullLink
        link={{
          to: routes.WORKSPACE_SUBSCRIBE.create({
            workspaceId: workspace.identifier,
          }),
        }}
        icon={LicenseIconMonochrome}
        disableLink={!canManageSubscription(workspace.userRole)}
      >
        <S.ContentContainer>
          <span>{`${daysLeft} days left `}</span>
          <S.FontWeightRegular>{`of your trial`}</S.FontWeightRegular>
        </S.ContentContainer>
      </NoticeContentTemplateFullLink>
    </WorkspaceSidebarNoticeContainer>
  )
}

type ActiveTrialFewDaysLeftProps = {
  daysLeft: number
  elapsedTime: ElapsedTime
  workspace: WorkspaceMinimalFragment
}
function ActiveTrialFewDaysLeft({
  daysLeft,
  elapsedTime,
  workspace,
}: ActiveTrialFewDaysLeftProps) {
  // Use 'full' because of https://sketch.slack.com/archives/C04N2EPHUGN/p1682001080112139.
  const hoursLeft = elapsedTime.toHours({ precision: 'full' })

  // Show the result in hours if less than 1 day
  const timeLeft =
    hoursLeft < 24
      ? createStringWithTimeUnit(hoursLeft, 'hour')
      : createStringWithTimeUnit(daysLeft, 'day')

  return (
    <WorkspaceSidebarNoticeContainer>
      <NoticeContentTemplateWithOptionalActionAndLearnMore
        action={
          canManageSubscription(workspace.userRole)
            ? {
                label: 'Subscribe',
                link: {
                  to: routes.WORKSPACE_SUBSCRIBE.create({
                    workspaceId: workspace.identifier,
                  }),
                },
              }
            : undefined
        }
        learnMoreLink={{
          href: `${SKETCH_WEBSITE}/pricing`,
          external: true,
        }}
        icon={LicenseIconMonochrome}
      >
        <S.ContentContainer>
          <span> {`${timeLeft} left of your trial`}</span>
          <p>
            <S.FontWeightRegular>Subscribe to keep going!</S.FontWeightRegular>
          </p>
        </S.ContentContainer>
      </NoticeContentTemplateWithOptionalActionAndLearnMore>
    </WorkspaceSidebarNoticeContainer>
  )
}

type EndedTrialProps = {
  workspace: WorkspaceMinimalFragment
}
function EndedTrial({ workspace }: EndedTrialProps) {
  return (
    <WorkspaceSidebarNoticeContainer variant="black">
      <NoticeContentTemplateWithOptionalActionAndLearnMore
        action={
          canManageSubscription(workspace.userRole)
            ? {
                label: 'Subscribe',
                link: {
                  to: routes.WORKSPACE_SUBSCRIBE.create({
                    workspaceId: workspace.identifier,
                  }),
                },
              }
            : undefined
        }
        learnMoreLink={{
          href: `${SKETCH_WEBSITE}/pricing`,
          external: true,
        }}
        icon={ExclamationMarkTriangleMonochromeIcon}
      >
        <S.ContentContainer>
          <div>Your trial has expired</div>
          <S.FontWeightRegular>Subscribe to keep going!</S.FontWeightRegular>
        </S.ContentContainer>
      </NoticeContentTemplateWithOptionalActionAndLearnMore>
    </WorkspaceSidebarNoticeContainer>
  )
}
